import React, { useState } from 'react'
import { XIcon } from 'lucide-react'
import type { PropertyNote } from './types'

interface PropertyNotesFieldsProps {
  propertyNotes: PropertyNote[]
  onPropertyNotesChange: (notes: PropertyNote[]) => void
  showActivityPlaceholder?: boolean
  compact?: boolean
}

export function PropertyNotesFields({
  propertyNotes,
  onPropertyNotesChange,
  showActivityPlaceholder = true,
  compact,
}: PropertyNotesFieldsProps) {
  const [notesDraft, setNotesDraft] = useState('')

  const inputClass = compact
    ? 'flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm'
    : 'flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500'

  const addNote = () => {
    if (!notesDraft.trim()) return
    onPropertyNotesChange([
      { timestamp: new Date().toISOString(), note: notesDraft.trim() },
      ...propertyNotes,
    ])
    setNotesDraft('')
  }

  const removeNote = (timestamp: string) => {
    onPropertyNotesChange(propertyNotes.filter((n) => n.timestamp !== timestamp))
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <textarea
          value={notesDraft}
          onChange={(e) => setNotesDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && e.ctrlKey) addNote()
          }}
          rows={3}
          className={inputClass}
          placeholder="Add a note... (Ctrl+Enter to submit)"
        />
        <button
          type="button"
          onClick={addNote}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors self-start"
        >
          Add Note
        </button>
      </div>
      <div className="space-y-3">
        {propertyNotes.length > 0 ? (
          propertyNotes
            .slice()
            .reverse()
            .map((note) => (
              <div key={note.timestamp} className="flex gap-3 bg-gray-50 rounded-lg p-3">
                <div className="flex-1">
                  <p className="text-sm text-gray-700">{note.note}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(note.timestamp).toLocaleString()}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => removeNote(note.timestamp)}
                  className="p-1 hover:bg-red-50 rounded transition-colors"
                >
                  <XIcon className="w-4 h-4 text-red-600" />
                </button>
              </div>
            ))
        ) : (
          <p className="text-sm text-gray-500 italic">No notes yet</p>
        )}
      </div>

      {showActivityPlaceholder && (
        <div className="border-t border-gray-200 pt-4 mt-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Activity</h4>
          <p className="text-sm text-gray-500 italic">
            Activities will appear here once the property is created.
          </p>
        </div>
      )}
    </div>
  )
}
